/**
 * "Is there probably a session?" — answered without loading any auth code.
 *
 * `/` has to be two things: the marketing landing page for a stranger, and the
 * workspace for a signed-in owner. Deciding which normally means asking Clerk,
 * and importing Clerk on `/` would put its bundle in front of every visitor —
 * which `scripts/acceptance.ts` BK-199-4 fails the build over, and rightly:
 * the public tree stays auth-free.
 *
 * So this is a *hint*, not an authority. It reads two things that are already
 * in the document and costs nothing:
 *
 *   - `__client_uat`, the cookie Clerk maintains precisely so a server or an
 *     edge can tell whether a session exists without running Clerk. It is not
 *     httpOnly by design, and a value other than `0` means signed in.
 *   - the handoff token in sessionStorage, which is how localhost and preview
 *     origins hold a session (they cannot read Clerk's cookie at all).
 *
 * A false positive costs a lazy chunk load and then the real auth gate says
 * signed out — recoverable. A false negative shows a signed-in owner the
 * landing page, which is why the cookie check is deliberately generous.
 *
 * Nothing here is a security decision. Every authenticated read is still
 * gated by the API rejecting a bad token.
 */

import { useSyncExternalStore } from "react";

const CLERK_UAT_COOKIE = "__client_uat";
const HANDOFF_STORAGE_KEY = "manticscore.editor.session.v1";

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  for (const part of document.cookie.split(";")) {
    const [key, ...rest] = part.trim().split("=");
    if (key === name) return rest.join("=");
  }
  return null;
}

/** Clerk writes `0` when signed out, a unix timestamp when signed in. */
export function hasClerkSessionCookie(cookie: string | null): boolean {
  if (!cookie) return false;
  const value = cookie.trim();
  if (!value || value === "0") return false;
  return Number.parseInt(value, 10) > 0;
}

export function hasStoredHandoffToken(raw: string | null): boolean {
  if (!raw) return false;
  try {
    const parsed = JSON.parse(raw) as { token?: unknown };
    return typeof parsed.token === "string" && parsed.token.length > 0;
  } catch {
    return false;
  }
}

/**
 * Whether `/` should render the workspace rather than the landing page.
 *
 * Synchronous on purpose: an async check would flash the landing page at an
 * owner on every load.
 */
export function hasSessionHint(): boolean {
  if (typeof window === "undefined") return false;
  // The first visit after hosted sign-in has not stored its token yet. The
  // lazy auth island must mount to consume it and remove it from the URL.
  if (new URLSearchParams(window.location.hash.slice(1)).get("__session")) return true;
  if (hasClerkSessionCookie(readCookie(CLERK_UAT_COOKIE))) return true;
  try {
    return hasStoredHandoffToken(window.sessionStorage.getItem(HANDOFF_STORAGE_KEY));
  } catch {
    // Private browsing can throw on sessionStorage access.
    return false;
  }
}

function subscribe(listener: () => void) {
  window.addEventListener("focus", listener);
  window.addEventListener("pageshow", listener);
  window.addEventListener("storage", listener);
  window.addEventListener("hashchange", listener);
  return () => {
    window.removeEventListener("focus", listener);
    window.removeEventListener("pageshow", listener);
    window.removeEventListener("storage", listener);
    window.removeEventListener("hashchange", listener);
  };
}

/** A navigation hint only; the auth island and API still verify the session. */
export function useSessionHint(): boolean {
  return useSyncExternalStore(subscribe, hasSessionHint, () => false);
}
